import { useState } from "react";
import { Pencil, ShieldCheck, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, PageHeader, Panel, RowsSkeleton, StatusBadge } from "@/components/app/kit";
import { FormDialog } from "@/components/app/FormDialog";
import { InviteNotice } from "@/pages/platform/TenantNew";
import { useFetch } from "@/hooks/useServerList";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { t, fmtDate } from "@/lib/i18n";

const ROLES = [["platform_admin", "Administrateur plateforme"], ["super_admin", "Super administrateur"]];

export default function Admins() {
  const { me } = useAuth();
  const isSuper = me.user.platform_role === "super_admin";
  const { data, loading, reload } = useFetch("/platform/admins");
  const [invite, setInvite] = useState(false);
  const [edit, setEdit] = useState(null);
  const [link, setLink] = useState(null);
  return (
    <div>
      <PageHeader eyebrow="Administration" title="Utilisateurs administrateurs" subtitle="Comptes ayant accès à l'espace Super Administration. Ils n'ont aucun accès aux données des cabinets."
        actions={isSuper && <Button onClick={() => setInvite(true)} data-testid="admins-invite-button"><UserPlus className="h-4 w-4 mr-2" />Inviter un administrateur</Button>} />
      {link && <div className="mb-6"><InviteNotice url={link.url} email={link.email} /></div>}
      <Panel className="overflow-hidden">
        <div className="-m-5 overflow-x-auto">
          {loading ? <RowsSkeleton /> : !data?.length ? <EmptyState icon={ShieldCheck} title="Aucun administrateur" /> : (
            <Table>
              <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>E-mail</TableHead><TableHead>Rôle</TableHead><TableHead>Créé le</TableHead><TableHead>Statut</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {data.map((u) => (
                  <TableRow key={u.id} data-testid={`admin-row-${u.email}`}>
                    <TableCell className="font-medium">{u.name}{u.id === me.user.id && <span className="text-xs text-slate-400"> (vous)</span>}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell className="text-sm">{t(`role.${u.platform_role}`)}</TableCell>
                    <TableCell className="text-sm text-slate-500">{fmtDate(u.created_at)}</TableCell>
                    <TableCell><StatusBadge status={u.status} /></TableCell>
                    <TableCell>{isSuper && u.id !== me.user.id && <Button variant="ghost" size="sm" onClick={() => setEdit(u)} data-testid={`admin-edit-${u.email}`}><Pencil className="h-4 w-4" /></Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Panel>
      <FormDialog open={invite} onOpenChange={setInvite} testid="admin-invite-dialog" title="Inviter un administrateur" submitLabel="Générer l'invitation"
        initial={{ platform_role: "platform_admin" }}
        fields={[{ key: "name", label: "Nom complet", required: true }, { key: "email", label: "E-mail", type: "email", required: true },
          { key: "platform_role", label: "Rôle", type: "select", options: ROLES, required: true, full: true }]}
        onSubmit={async (v) => { const { data: r } = await api.post("/platform/admins", v); setLink({ url: r.invite_url, email: v.email }); toast.success("Invitation générée"); reload(); }} />
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} testid="admin-edit-dialog" title={`Modifier ${edit?.name}`}
        description="La désactivation ferme immédiatement toutes ses sessions."
        initial={{ platform_role: edit?.platform_role, status: edit?.status === "disabled" ? "disabled" : "active" }}
        fields={[{ key: "platform_role", label: "Rôle", type: "select", options: ROLES, required: true }, { key: "status", label: "Statut", type: "select", options: [["active", "Actif"], ["disabled", "Désactivé"]], required: true }]}
        onSubmit={async (v) => { await api.put(`/platform/admins/${edit.id}`, v); toast.success("Administrateur mis à jour"); reload(); }} />
    </div>
  );
}
